import type { ISnippetRule, SnippetType } from '@carboncopyco/types';
import {
  Button,
  InputText,
  LoadingDots,
  TextCaption,
  notify,
} from '@carboncopyco/ui';
import { useBoolean } from 'ahooks';
import classnames from 'classnames';
import { isEqual } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';
import { CodeEditor, type MonacoEditor } from '../../CodeEditor';
import { InputField } from '../../InputField';

const snippetTypeToLanguage: Record<SnippetType, string> = {
  css: 'css',
  js: 'javascript',
};

// TS6133 - declared but its value is never read
const unusedLocalVariableErrorCode = 6133;
const ignoredCodeEditorErrors = [unusedLocalVariableErrorCode];

export type CustomCodeCreationFormProps = {
  type: SnippetType;
  snippet?: ISnippetRule;
  snippets?: ISnippetRule[];
  autoFocus?: boolean;
  onFocus?: () => void;
  onSubmit: (snippet: ISnippetRule) => Promise<void>;
  onCancel?: () => void;
  disabled?: boolean;
};

export const CustomCodeCreationForm = ({
  type,
  snippet,
  snippets = [],
  autoFocus,
  onFocus,
  onSubmit,
  onCancel,
  disabled,
}: CustomCodeCreationFormProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<MonacoEditor>(null);
  const [isSubmitting, setIsSubmitting] = useBoolean(false);
  const [name, setName] = useState(snippet?.name ?? '');
  const [code, setCode] = useState('');
  const [nameError, setNameError] = useState(null);
  const [codeEditorError, setCodeEditorError] = useState(null);
  const prevCode = useRef(null);

  const getSourceCode = async () => {
    try {
      const response = await fetch(snippet.source);
      const text = await response.text();
      prevCode.current = text;
      setCode(text);
    } catch (_error) {
      return code;
    }
  };

  useEffect(() => {
    if (snippet) {
      getSourceCode();
    }
  }, [snippet]);

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
  }, [autoFocus]);

  const onSubmitHandler = async () => {
    if (isSnippetAlreadyExists()) {
      setNameError('Snippet with this name already exists.');
      return;
    }

    try {
      setIsSubmitting.setTrue();
      await onSubmit({
        ...(snippet ?? {}),
        source: code,
        name,
        snippetType: type,
        isActive: snippet?.isActive ?? true,
      } as ISnippetRule);
    } catch (_error) {
      notify.error(
        `Failed to ${snippet ? 'update' : 'create'} snippet, please try again.`
      );
    } finally {
      setIsSubmitting.setFalse();
    }
  };

  function isSnippetAlreadyExists(): boolean {
    return snippets
      ?.filter(({ name }) => name !== snippet?.name)
      .some((snippet) => snippet.name === name);
  }

  function isFormValid(): boolean {
    // In case of editing a snippet, check if the form has changed.
    const isFormHasNotChanged =
      snippet &&
      isEqual({ name: snippet.name, code: prevCode.current }, { name, code });

    if (isFormHasNotChanged) return false;

    // Due to backend limitation, the code content should be at least 3 characters.
    const isCodeLengthValid = code.length > 2;
    if (!isCodeLengthValid) return false;

    return name.length && !codeEditorError && !nameError;
  }

  const language = snippetTypeToLanguage[type] ?? snippetTypeToLanguage.js;

  return (
    <div className="relative">
      <div
        className={classnames('flex flex-col gap-4 relative', {
          'opacity-0': isSubmitting,
          'opacity-30': disabled,
        })}
      >
        <div className="flex flex-col gap-3.5">
          <InputField
            label="Snippet name"
            name="snippetName"
            error={
              nameError && (
                <TextCaption
                  color="text-ds-danger"
                  label={nameError}
                />
              )
            }
          >
            <InputText
              ref={inputRef}
              onFocus={onFocus}
              autoFocus={autoFocus}
              name="name"
              disabled={snippet && !snippet.isActive}
              size="lg"
              value={name}
              onChange={(e) => {
                const value = e.target.value;
                setNameError(null);
                setName(value);
              }}
              state={nameError ? 'error' : 'normal'}
            />
          </InputField>
          <InputField
            label="Snippet"
            name="code"
            error={
              codeEditorError && (
                <TextCaption
                  color="text-ds-danger"
                  label={codeEditorError}
                />
              )
            }
          >
            <div
              className={classnames(
                'rounded-md border overflow-hidden transition-all',
                codeEditorError
                  ? 'border-ds-danger-400'
                  : 'border-ds-natural-150'
              )}
            >
              <CodeEditor
                options={{
                  readOnly: snippet && !snippet.isActive,
                }}
                onMount={(editor, monaco) => {
                  editorRef.current = editor;
                  monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions(
                    {
                      diagnosticCodesToIgnore: ignoredCodeEditorErrors,
                    }
                  );
                }}
                onChange={(value) => {
                  setCode(value);
                }}
                value={code}
                language={language}
                onError={(errors) => {
                  if (!errors.length) {
                    setCodeEditorError(null);
                    return;
                  }

                  setCodeEditorError(errors[0].message);
                }}
              />
            </div>
          </InputField>
        </div>
        <div className="flex flex-row gap-2.5 justify-end">
          <Button
            label="Cancel"
            onClick={onCancel}
            highContrast
          />
          <Button
            label={snippet ? 'Save' : `Add ${type.toUpperCase()} snippet`}
            onClick={onSubmitHandler}
            disabled={!isFormValid()}
          />
        </div>
      </div>
      {isSubmitting && (
        <div className="absolute top-0 left-0 w-full h-full flex flex-col justify-center items-center">
          <LoadingDots />
        </div>
      )}
    </div>
  );
};
